import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
  Box,
  Alert,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"
import {
  PostcodeLookupAddress,
  AddressDetails,
  CustomerAddress,
} from "../types/address";
import { borderRadius, typography } from "../theme";
import { useApiClient } from "../useApiClient";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useRoles } from "../useRoles";

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onAddressSelect: (address: CustomerAddress) => void;
  onAddAddress: () => void;
}

export const AddressLookupModal: React.FC<Props> = ({
  isVisible,
  onClose,
  onAddressSelect,
  onAddAddress,
}) => {
  const roles = useRoles();
  const isSuperUser = roles.includes("SuperUser");

  const [postcode, setPostcode] = useState("");
  const [addresses, setAddresses] = useState<PostcodeLookupAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const standardUserAddressNotFoundMessage =
    "No address could be found for this postcode. Please contact a Super User if a new address needs to be added.";

  const mapToCustomerAddress = (details: AddressDetails): CustomerAddress => ({
    externalId: crypto.randomUUID(),
    isActive: true,
    isPrimary: false,
    addressType: "Home", // Default type
    nameNumber: details.number,
    street: details.street,
    town: details.town,
    postcode: details.postcode,
    // : details.buildingId,
    country: "UK", // Default for UK postcode lookup
  });
  const { callApi } = useApiClient();
  // Reset form when modal is opened
  React.useEffect(() => {
    if (isVisible) {
      setPostcode("");
      setAddresses([]);
      setError(null);
    }
  }, [isVisible]);
  const searchPostcode = async () => {
    if (!postcode.trim()) {
      setError("Please enter a postcode");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.POSTCODE_SEARCH, postcode),
        {
          method: "GET",
        }
      );

      if (!Array.isArray(response.data)) {
        setError("Invalid response format from server");
        return;
      }

      setAddresses(response.data);
      if (!response.data.length) {
        setError(
          isSuperUser
            ? "No addresses found for this postcode"
            : standardUserAddressNotFoundMessage
        );
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setError(
        isSuperUser
          ? "Failed to fetch addresses. Please try again."
          : standardUserAddressNotFoundMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: PostcodeLookupAddress): string => {
    const parts = [
      address.subBuilding,
      address.number,
      address.street,
      address.town,
      address.postcode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const fetchAddressDetails = async (
    lookupAddress: PostcodeLookupAddress
  ): Promise<AddressDetails> => {
    const { data } = await callApi(
      `${getApiUrl(
        API_CONFIG.ENDPOINTS.POSTCODE_DETAILS
      )}?postcode=${encodeURIComponent(
        lookupAddress.postcode
      )}&buildingnumber=${encodeURIComponent(lookupAddress.number)}&buildingid=${encodeURIComponent(lookupAddress.buildingId)}`,
      { method: "GET" }
    );
    return data;
  };

  const onAddressClick = async (lookupAddress: PostcodeLookupAddress) => {
    try {
      setLoading(true);
      setError(null);
      const addressDetails = await fetchAddressDetails(lookupAddress);
      const customerAddress = mapToCustomerAddress(addressDetails);
      onAddressSelect(customerAddress);
      onClose();
    } catch (err) {
      setError("Failed to fetch address details. Please try again.");
      console.error("Error fetching address details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      searchPostcode();
    }
  };

  return (
    <Dialog open={isVisible} onClose={onClose} maxWidth="md" fullWidth
      sx={{
              '& .MuiPaper-root': {
              borderRadius: borderRadius.small,
          },
      }}
    >
      <Box sx={{display:"flex", justifyContent:"space-between"}}>
        <DialogTitle sx={{
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.fontSize.xxl,
            fontStyle: "normal",
            fontWeight: typography.fontWeight.bold,
            lineHeight: "24px",
            mt: "20px",
            ml: "20px"
        }}>
          Address Lookup
        </DialogTitle>
        <IconButton onClick={onClose} sx={{height:"40px", mt:"24px", mr:"30px"}}>
            <CloseIcon/>
        </IconButton>
      </Box>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1, m: 2, mt:'-10px' }}>
          <TextField
            autoFocus
            placeholder="E.g. 'CR0 3RL' or '36 Factory Lane'"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            onKeyPress={handleKeyPress}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={searchPostcode}
            disabled={loading}
            sx={{
                fontFamily: typography.fontFamily.primary,
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.medium,
                textTransform: 'none'
            }}>
            Search
          </Button>
        </Box>

        {error && (
          <Box sx={{ m:2 }}>
            <Alert severity="error">
              {error}
            </Alert>
            {isSuperUser && (
              <Link 
                sx={{
                  ml:'4px',
                  cursor: 'pointer'
                }}
                onClick={onAddAddress}
              >
                Add new address
              </Link>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: "auto", m:2}}>
            {addresses.map((address, index) => (
              <ListItem
                key={index}
                onClick={() => onAddressClick(address)}
                divider
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ListItemText primary={formatAddress(address)} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      {/* <DialogActions sx={{display:"flex", flexFlow:"row-reverse"}}>
        <Button 
            onClick={onClose}
            variant="outlined"
            sx={{
                    borderColor: 'black',
                    color: 'black',
                    fontWeight: 'bold',
                    borderWidth: 2,
                    textTransform: 'none',
                    '&:hover': {
                    borderColor: 'black',
                    }
                }}>
                  Cancel
        </Button>
        <Button onClick={onAddAddress}>Add new address</Button>

      </DialogActions> */}
    </Dialog>
  );
};
